/**
 * @file ShopsPage.tsx
 * @description «Платформа → Магазины» (phase19.28 этап А, v0.19.63;
 * uiux-platform-shops §2, §7; PL1, PL8, SB138, В4).
 *
 * Список магазинов платформы: поиск по названию, slug и адресу (на
 * сервере), отборы «Все / Есть проблемы / Сайт закрыт», таблица от 1024 и
 * строки-карточки на телефоне (вся строка — ссылка на карточку магазина).
 * Сверху — полоса о неизвестных адресах за 7 дней со шторкой журнала.
 * Главное действие «Создать магазин» — в шапке, на телефоне закреплено внизу.
 * Право раздела проверяет маршрут (`ShopsAccess`).
 */

import { Plus, Search, Store } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { SHOPS_PAGE_SIZE, useShopsList } from "../../../hooks/platform/useShopsList";
import { pluralRu } from "../../../lib/plural";
import { FILTER_EMPTY_TEXT, listHasChecks } from "../../../lib/platform/shop-list-filter";
import { isIpHost } from "../../../lib/platform/shop-domain-status";
import { usePlatformEdgeConfig } from "../../../hooks/platform/usePlatformEdgeConfig";
import { shopColumns } from "../../../components/platform/shops/shop-columns";
import { UnknownDomainsSheet } from "../../../components/platform/shops/UnknownDomainsSheet";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { FilterChips } from "../../../components/ui/FilterChips";
import { ListPagination } from "../../../components/ui/ListPagination";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { SearchInput } from "../../../components/ui/SearchInput";

const NEW_SHOP_PATH = "/platform/shops/new";
/** Сервер отдаёт не больше 100 строк списка. */
const SHOPS_SERVER_LIMIT = 100;

function CreateButton() {
  return (
    <Button variant="brand" asChild>
      <Link to={NEW_SHOP_PATH}>
        <Plus className="size-4" aria-hidden />
        Создать магазин
      </Link>
    </Button>
  );
}

export function ShopsPage() {
  const state = useShopsList();
  const [sheetOpen, setSheetOpen] = useState(false);
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const { list, items, rows, pageRows } = state;
  const unknownItems = state.unknown.data ?? [];
  const unknownDomains = unknownItems.filter((item) => !isIpHost(item.host));
  const total = items.length;
  // Поиск — «Найдено N», без поиска — сколько всего (ревью М6).
  const searching = state.search.trim() !== "";
  const subtitle = !list.data
    ? undefined
    : searching
      ? `Найдено ${total}`
      : `${total} ${pluralRu(total, ["магазин", "магазина", "магазинов"])} на платформе`;
  const { baseDomain } = usePlatformEdgeConfig();
  const emptyDescription = `Новый получит адрес ‹slug›.${baseDomain} и пустой сайт в теме платформы.`;

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader title="Магазины" subtitle={subtitle} action={<CreateButton />} />

      {unknownDomains.length > 0 ? (
        <NoticeStrip
          tone="warning"
          action={
            <Button variant="outline" onClick={() => setSheetOpen(true)}>
              Посмотреть
            </Button>
          }
        >
          {unknownDomains.length} {pluralRu(unknownDomains.length, ["неизвестный домен", "неизвестных домена", "неизвестных доменов"])} за 7 дней
          {compact ? null : `: ${unknownDomains.slice(0, 3).map((d) => d.hostUnicode || d.host).join(", ")}`}
        </NoticeStrip>
      ) : null}

      <div className="flex flex-col gap-3">
        <SearchInput
          value={state.search}
          onChange={state.setSearch}
          placeholder="Название, slug, домен"
          ariaLabel="Поиск магазинов"
          className="max-w-md"
        />
        {list.data ? (
          <FilterChips aria-label="Отбор магазинов" items={state.chips} value={state.filter} onChange={state.setFilter} />
        ) : null}
      </div>

      <ResponsiveList
        aria-label="Магазины платформы"
        rows={pageRows}
        columns={shopColumns(listHasChecks(items))}
        rowKey={(shop) => shop.id}
        rowHref={(shop) => `/platform/shops/${shop.id}`}
        rowLabel={(shop) => `Магазин «${shop.displayName}»`}
        rowSize="double"
        compactRowSize="tall"
        compactWrap
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        filtered={state.filtered}
        empty={{
          icon: Store,
          title: "Магазинов пока нет",
          description: emptyDescription,
          action: <CreateButton />,
        }}
        filteredEmpty={{
          icon: Search,
          title: FILTER_EMPTY_TEXT[state.filter],
          onReset: () => {
            state.setSearch("");
            state.setFilter("all");
          },
        }}
      />

      {rows.length > SHOPS_PAGE_SIZE ? (
        <ListPagination
          total={rows.length}
          pageIndex={state.page}
          pageSize={SHOPS_PAGE_SIZE}
          pageSizeOptions={[SHOPS_PAGE_SIZE]}
          onPageSizeChange={() => undefined}
          onPrev={() => state.setPage(state.page - 1)}
          onNext={() => state.setPage(state.page + 1)}
          canPrev={state.page > 0}
          canNext={(state.page + 1) * SHOPS_PAGE_SIZE < rows.length}
        />
      ) : null}

      {total >= SHOPS_SERVER_LIMIT ? (
        <p className="type-caption text-on-surface-variant">Показаны первые {SHOPS_SERVER_LIMIT} — уточните поиск.</p>
      ) : null}

      {total === 1 && !state.filtered ? (
        <p className="type-caption text-on-surface-variant">
          Пока один магазин. {emptyDescription}
        </p>
      ) : null}

      <UnknownDomainsSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        items={unknownItems}
        loading={state.unknown.loading}
        error={state.unknown.error}
        onRetry={state.unknown.reload}
        shops={items}
        onBound={() => {
          state.unknown.reload();
          list.reload();
        }}
      />
    </div>
  );
}
